import React from "react";
import { ArrowRight, Building2, Home as HomeIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Section, Kicker, Heading, Button, Rule } from "../components/ui";
import Reveal from "../components/Reveal";
import Logo from "../components/Logo";

const STATS = [
  { value: "2", label: "Established brands" },
  { value: "2018", label: "OTP Group founded" },
  { value: "Decades", label: "Of combined brand legacy" },
  { value: "UK-wide", label: "Residential focus" },
];

const Home = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative bg-navy text-white min-h-[92vh] flex items-center pt-28 pb-20 overflow-hidden">
        {/* subtle motif */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none hidden md:block">
          <div className="flex items-end gap-6">
            <div className="w-16 h-40 rounded-full bg-white" />
            <div className="w-16 h-64 rounded-full bg-white" />
            <div className="w-16 h-96 rounded-full bg-steel-light" />
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 w-full relative">
          <Reveal>
            <Logo variant="light" size={56} className="mb-10" />
          </Reveal>
          <Reveal delay={0.1}>
            <Heading as="h1" className="text-4xl md:text-6xl lg:text-7xl max-w-4xl">
              A long-term home for agencies and property portfolios.
            </Heading>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg md:text-xl text-steel-light max-w-2xl leading-relaxed">
              OTP Group acquires and holds established letting and estate agencies and residential
              property portfolios across the United Kingdom. We build carefully, hold for the long
              term, and support the businesses and brands that join us.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Button to="/agency-acquisitions" variant="primaryLight" testid="hero-cta-primary">
                Agency acquisitions <ArrowRight size={16} />
              </Button>
              <Button to="/property-portfolios" variant="outlineLight" testid="hero-cta-secondary">
                Property portfolios
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Proof strip */}
      <section className="bg-stone border-b border-navy/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
            {STATS.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="text-center lg:text-left" data-testid={`stat-${i}`}>
                  <div className="text-4xl md:text-5xl font-light tracking-heading text-navy">
                    {s.value}
                  </div>
                  <div className="mt-2 text-xs tracking-kicker uppercase text-steel">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Two pillars */}
      <Section>
        <Reveal>
          <div className="max-w-2xl">
            <Kicker>What we do</Kicker>
            <Heading className="text-3xl md:text-4xl">
              Two disciplined acquisition pillars.
            </Heading>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[
            {
              icon: Building2,
              title: "Estate & Letting Agency Acquisitions",
              text: "We acquire estate and letting agencies from owners looking for a considered exit. Teams, clients and local reputation are retained, and the business continues under experienced long-term ownership.",
              to: "/agency-acquisitions",
              testid: "pillar-agency",
            },
            {
              icon: HomeIcon,
              title: "Property Portfolio Acquisitions",
              text: "We acquire residential property portfolios and hold them for the long term. Vendors receive a clean, certain transaction and tenants experience uninterrupted, professional management.",
              to: "/property-portfolios",
              testid: "pillar-portfolio",
            },
          ].map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.testid} delay={i * 0.1}>
                <Link
                  to={p.to}
                  data-testid={p.testid}
                  className="group block h-full border border-navy/10 p-10 lg:p-12 hover:bg-navy hover:border-navy transition-colors duration-300"
                >
                  <Icon size={32} strokeWidth={1.5} className="text-steel group-hover:text-steel-light transition-colors" />
                  <h3 className="mt-8 text-2xl font-semibold tracking-heading group-hover:text-white transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-steel leading-relaxed group-hover:text-steel-light transition-colors">
                    {p.text}
                  </p>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-navy group-hover:text-white transition-colors">
                    Learn more <ArrowRight size={16} />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Track record teaser */}
      <Section stone>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Kicker>Our approach</Kicker>
              <Heading className="text-3xl md:text-4xl max-w-xl">
                We grow deliberately, and we hold what we buy.
              </Heading>
              <Rule className="mt-6" />
              <p className="mt-6 text-steel leading-relaxed max-w-xl">
                OTP Group is a privately owned holding company. We fund acquisitions through arranged
                debt and deferred consideration rather than external investment, which allows us to
                move with certainty and to take a genuinely long-term view of every business and
                portfolio we acquire.
              </p>
              <div className="mt-8">
                <Button to="/about" variant="outline" testid="home-about-cta">
                  About OTP Group <ArrowRight size={16} />
                </Button>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="aspect-[4/5] w-full overflow-hidden border border-navy/10">
                <img
                  src="https://images.unsplash.com/photo-1510265236892-329bfd7de7a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxCcml0aXNoJTIwcmVzaWRlbnRpYWwlMjBzdHJlZXQlMjBob3VzZXN8ZW58MHx8fHwxNzg1NDIwOTQ2fDA&ixlib=rb-4.1.0&q=80&w=800&h=1000&fit=crop"
                  alt="A quiet residential mews of period townhouses"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Careers teaser */}
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <Kicker light>Careers</Kicker>
              <Heading className="text-3xl md:text-4xl">
                Build a career with a serious, growing group.
              </Heading>
              <p className="mt-4 text-steel-light leading-relaxed">
                We are a small, disciplined team. As the group grows, so do the opportunities to take
                on responsibility and help shape how we operate.
              </p>
            </div>
            <Button to="/careers" variant="primaryLight" testid="home-careers-cta" className="shrink-0">
              View careers <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
